import { Box, Divider, Paper, Typography } from '@mui/material';
import type IEmployeeTalkTemplate from '@sharedInterfaces/IEmployeeTalkTemplate';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import {
    createEmployeeTalkTemplate,
    deleteEmployeeTalkTemplate,
    editEmployeeTalkTemplate,
    getEmployeeTalkTemplates,
} from '@src/APIs/graphQl/EmployeeTalkTemplate';
import { useConfirmDeleteDialog } from '@src/App/Components/Dialogs/ConfirmDelete/confirmDelete';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import { ETTTemplateEditor, ETTTemplateManager } from '@src/App/Components/Settings/EmployeeTalkTemplate';
import type { ITemplate } from '@src/App/Components/Settings/EmployeeTalkTemplate/TemplateEditor/types';
import { addETT, deleteETT, updateETT } from '@store/reducer/employeeTalkTemplatesReducer';
import type { AppState } from '@store/store';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type TabValue = 'list' | 'edit';

export function EmployeeTalkTemplateTab() {
    const dispatch = useDispatch();
    const showErrorDialog = useErrorDialog();
    const confirmDelete = useConfirmDeleteDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;

    const templates = useSelector((state: AppState) => state.employeeTalkTemplates);
    const permissions = useSelector((state: AppState) => state.permissions);
    const canCreate = checkPermissions('EmployeeTalkTemplates', 'Add', permissions);
    const canEdit = checkPermissions('EmployeeTalkTemplates', 'Edit', permissions);
    const canDelete = checkPermissions('EmployeeTalkTemplates', 'Delete', permissions);

    const [currentTab, setCurrentTab] = useState<TabValue>('list');
    const [currentTemplateId, setCurrentTemplateId] = useState<number | null>(null);

    useEffect(() => {
        const loadTemplates = async () => {
            await getEmployeeTalkTemplates().catch((error) => {
                console.error('Error loading templates:', error);
                showErrorDialog(error);
            });
        };
        loadTemplates();
    }, []);
    const convertToUITemplates = (templates: IEmployeeTalkTemplate[]): ITemplate[] => {
        return templates?.map((t) => ({
            id: t.id,
            title: t.title,
            description: t.description,
            templateVersion: t.templateVersion,
            pages: t.pages,
            isActive: t.isActive,
        })) as ITemplate[];
    };
    const uiTemplates = useMemo(() => convertToUITemplates(templates), [templates]);

    const currentTemplate = currentTemplateId ? templates?.find((t) => t.id === currentTemplateId) : null;

    // Konvertiere IEmployeeTalkTemplate zu Template für die UI

    const handleCreateTemplate = async (templateData: Omit<ITemplate, 'id'>) => {
        await createEmployeeTalkTemplate(
            templateData.title,
            templateData.description,
            templateData.pages,
            templateData.isActive,
        ).then((result) => {
            if (result) {
                dispatch(addETT(result));
                // Wechsle zum Editor für das neue Template
                setCurrentTemplateId(result.id);
                setCurrentTab('edit');
            }
        });
    };

    const handleUpdateTemplate = async (updatedTemplate: ITemplate) => {
        const result = await editEmployeeTalkTemplate(
            updatedTemplate.id,
            updatedTemplate.title,
            updatedTemplate.description,
            updatedTemplate.templateVersion,
            updatedTemplate.pages,
            updatedTemplate.isActive,
        );

        if (result) {
            // console.log(result);
            dispatch(updateETT(result));
            // Zurück zur Liste nach dem Speichern
            // setCurrentTab('list');
        }
    };

    const handleDeleteTemplate = async (id: number) => {
        const template = templates?.find((t) => t.id === id);
        if (!template) return;

        const confirmed = await confirmDelete(template.title, langStrings.confirmDeleteTemplate);
        if (confirmed) {
            try {
                const result = await deleteEmployeeTalkTemplate(id);

                if (result?.deleteEmployeeTalkTemplate) {
                    dispatch(deleteETT(id));

                    if (currentTemplateId === id) {
                        setCurrentTemplateId(null);
                        setCurrentTab('list');
                    }
                }
            } catch (error) {
                console.error('Error deleting template:', error);
            }
        }
    };

    const handleEditTemplate = (id: number) => {
        setCurrentTemplateId(id);
        setCurrentTab('edit');
    };

    const handleBackToList = () => {
        setCurrentTab('list');
        setCurrentTemplateId(null);
    };

    if (!templates) return <LoadingBox />;

    return (
        <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {langStrings.templates}
                </Typography>
                <Divider />

                {currentTab === 'list' ? (
                    <ETTTemplateManager
                        templates={uiTemplates}
                        onCreateTemplate={canCreate ? handleCreateTemplate : undefined}
                        onUpdateTemplate={canEdit ? handleUpdateTemplate : undefined}
                        onDeleteTemplate={canDelete ? handleDeleteTemplate : undefined}
                        onEditTemplate={canEdit ? handleEditTemplate : undefined}
                    />
                ) : (
                    currentTemplate && (
                        <>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Box
                                    component="span"
                                    onClick={handleBackToList}
                                    sx={{ cursor: 'pointer', textDecoration: 'underline', mr: 2 }}
                                >
                                    {langStrings.backToList}
                                </Box>
                            </Box>
                            <ETTTemplateEditor
                                initialTemplate={convertToUITemplates([currentTemplate])[0]}
                                onSave={handleUpdateTemplate}
                            />
                        </>
                    )
                )}
            </Box>
        </Paper>
    );
}
